import { MAP_STORY_PATH_COLOR } from "./mapDocument";
import type { StoryPathStop } from "./mapStoryPath";
import type { WorldPoint } from "./mapGeometry";
import { pathLineDash, traceSmoothPath } from "./mapGeometry";

const STOP_RADIUS_PX = 7;
const STOP_RADIUS_SELECTED_PX = 9;

export type StoryPathDrawOptions = {
  readonly zoom: number;
  readonly selectedKey?: string;
  /** Place keys (`landmark:id` / `marker:id`) to emphasize from sidebar hover. */
  readonly highlightedKeys?: readonly string[];
  readonly color?: string;
};

function placeKey(stop: Pick<StoryPathStop, "kind" | "id">): string {
  return `${stop.kind}:${stop.id}`;
}

/**
 * Draw the inferred story path (dashed) and numbered stop marks.
 * Only called while the Story path tool is active.
 */
export function drawStoryPath(
  ctx: CanvasRenderingContext2D,
  stops: readonly StoryPathStop[],
  toScreen: (p: WorldPoint) => { x: number; y: number },
  options: StoryPathDrawOptions,
): void {
  if (stops.length === 0) return;
  const color = options.color ?? MAP_STORY_PATH_COLOR;
  const zoom = options.zoom;
  const lineWidth = Math.max(2.5, 2.75 * Math.min(zoom, 2));
  const points = stops.map((s) => ({ x: s.x, y: s.y }));

  ctx.save();
  if (points.length >= 2) {
    ctx.strokeStyle = "rgba(250, 248, 240, 0.9)";
    ctx.lineWidth = lineWidth + 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.setLineDash([]);
    traceSmoothPath(ctx, points, toScreen);
    ctx.stroke();

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash(pathLineDash("dashed", lineWidth));
    traceSmoothPath(ctx, points, toScreen);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  const highlighted = new Set(options.highlightedKeys ?? []);

  for (const stop of stops) {
    const screen = toScreen(stop);
    const key = placeKey(stop);
    const selected =
      typeof options.selectedKey === "string" && options.selectedKey === key;
    const isHighlighted = highlighted.has(key);
    const emphasized = selected || isHighlighted;
    const radius = emphasized ? STOP_RADIUS_SELECTED_PX : STOP_RADIUS_PX;

    ctx.beginPath();
    ctx.arc(
      screen.x,
      screen.y,
      radius + (isHighlighted ? 4 : 2.5),
      0,
      Math.PI * 2,
    );
    ctx.fillStyle = isHighlighted
      ? "rgba(180, 120, 40, 0.35)"
      : "rgba(250, 248, 240, 0.95)";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = emphasized ? 2 : 1.25;
    ctx.strokeStyle = emphasized ? "#b47828" : "rgba(40, 32, 24, 0.55)";
    ctx.stroke();

    ctx.fillStyle = "#faf8f0";
    ctx.font = `600 ${Math.max(9, radius)}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(stop.index), screen.x, screen.y + 0.5);
  }
  ctx.restore();
}

export function storyPathStopHitRadiusWorld(zoom: number): number {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  return (STOP_RADIUS_PX + 4) / z;
}

export function storyPathPlaceKey(
  kind: "landmark" | "marker",
  id: string,
): string {
  return `${kind}:${id}`;
}
